import { Injectable } from '@angular/core';
import { ExecutionResult, LcsApiService } from '@lcs/shared/api';
import { OrderingOption } from 'generated/graphql-types';
import { Observable } from 'rxjs';
import { FilterExpression, FilterOption, serializeFilterExpression } from 'src/app/filters/filter-definition';
import { FilterOperations } from 'src/app/filters/filter-operation/filter-operations.enum';
import { FilterPrimitive } from 'src/app/filters/filter-primitives';
import { LogicalOperators } from 'src/app/filters/logical-operators.enum';
import { DateTimeFilterFormatPipe } from 'src/app/pipes/datetime-filter-format-pipe/datetime-filter-format-pipe';
import { SystemLogsFilterOption } from '../models/system-log-filter-option';
import { SystemLogOrderingOptions, SystemLogRegisterOptions } from '../models/system-log-register-options';
import { GetSystemLogRegister, GetSystemLogRegisterVariables } from '../queries/generated/GetSystemLogRegister';
import { getSystemLogQuery } from '../queries/getSystemLogRegister.graphql';

@Injectable({ providedIn: 'root' })
export class SystemLogRegisterApiService {
   private dateTimePipe: DateTimeFilterFormatPipe;

   constructor(private apiService: LcsApiService) {
      this.dateTimePipe = new DateTimeFilterFormatPipe();
   }

   getSystemLog(
      endCursor: string | null,
      filters: SystemLogsFilterOption[] | null,
      quickFilters: SystemLogRegisterOptions | null,
      orderingOptions: SystemLogOrderingOptions[] | null
   ): Observable<ExecutionResult<GetSystemLogRegister>> {
      const filterBuilderFilterExpression: FilterExpression | null =
         filters && filters.length > 0
            ? {
                 filterOptions: filters.map(this.convertSystemLogFilterOption.bind(this)),
              }
            : { filterOptions: [] };
      if (quickFilters) {
         if (quickFilters.severity) {
            filterBuilderFilterExpression.filterOptions.push({
               field: 'SeverityId',
               operation: FilterOperations.EqualTo,
               value: quickFilters.severity.name,
            });
         }

         if (quickFilters.systemLogArea) {
            filterBuilderFilterExpression.filterOptions.push({
               field: 'AreaId',
               operation: FilterOperations.EqualTo,
               value: quickFilters.systemLogArea.name,
            });
         }

         if (quickFilters.systemLogType) {
            filterBuilderFilterExpression.filterOptions.push({
               field: 'TypeId',
               operation: FilterOperations.EqualTo,
               value: quickFilters.systemLogType.name,
            });
         }

         if (quickFilters.startDate) {
            filterBuilderFilterExpression.filterOptions.push({
               field: 'CreateUserDate',
               operation: FilterOperations.GreaterThanOrEqualTo,
               value: this.dateTimePipe.transform(new Date(quickFilters.startDate)),
            });
         }

         if (quickFilters.account) {
            if (quickFilters.account.accountId) {
               filterBuilderFilterExpression.filterOptions.push({
                  field: 'Account.AccountId',
                  operation: FilterOperations.EqualTo,
                  value: quickFilters.account.accountId,
               });
            }
         }

         if (quickFilters.endDate) {
            filterBuilderFilterExpression.filterOptions.push({
               field: 'CreateUserDate',
               operation: FilterOperations.LessThanOrEqualTo,
               value: this.dateTimePipe.transform(new Date(quickFilters.endDate)),
            });
         }

         if (quickFilters.searchText) {
            const searchText = quickFilters.searchText.trim();
            const subExpression: FilterExpression = {
               filterOptions: [],
               logicalOperator: LogicalOperators.Or,
            };
            subExpression.logicalOperator = LogicalOperators.Or;
            subExpression.filterOptions.push({
               field: 'Message',
               operation: FilterOperations.Contains,
               value: searchText,
            });
            filterBuilderFilterExpression.subExpressions = [subExpression];
         }
      }

      const variables = this.getSystemLogVariables(endCursor, filterBuilderFilterExpression, orderingOptions);
      return this.apiService.query<GetSystemLogRegister, GetSystemLogRegisterVariables>({
         query: getSystemLogQuery,
         variables,
      });
   }

   private getSystemLogVariables(
      after: string | null,
      filterBuilderFilters: FilterExpression | null,
      orderingOptions: OrderingOption[] | null
   ): GetSystemLogRegisterVariables {
      const variables: GetSystemLogRegisterVariables = {
         after,
         filters: this.buildFilterExpression(filterBuilderFilters),
         first: 50,
         orderingOptions,
      };
      return variables;
   }

   private buildFilterExpression(filterBuilderFilters: FilterExpression | null): string {
      const expressions: FilterExpression[] = filterBuilderFilters?.subExpressions ?? [];

      if (filterBuilderFilters) {
         return serializeFilterExpression({
            filterOptions: filterBuilderFilters.filterOptions,
            logicalOperator: LogicalOperators.And,
            subExpressions: expressions,
         });
      }

      if (expressions.length > 1) {
         return serializeFilterExpression({
            filterOptions: [],
            logicalOperator: LogicalOperators.And,
            subExpressions: expressions,
         });
      } else if (expressions.length === 1) {
         return serializeFilterExpression(expressions[0]);
      } else {
         return '';
      }
   }

   private convertSystemLogFilterOption(option: SystemLogsFilterOption): FilterOption {
      return {
         field: option.field,
         operation: option.operation,
         value: this.getFilterOptionValue(option),
      };
   }

   private getFilterOptionValue(option: SystemLogsFilterOption): FilterPrimitive | FilterPrimitive[] | null {
      if (option.operation === FilterOperations.HasValue) {
         return option.value;
      }
      switch (option.type) {
         case 'systemLog':
            return Array.isArray(option.value)
               ? option.value.map((v) => {
                    return v.systemLogId;
                 })
               : option.value?.systemLogId || null;
         case 'text-box':
         case 'date':
         case 'numeric':
         case 'boolean':
            return option.value;
      }
   }
}
